import React from 'react';
import { Button, PlusIcon } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

interface EmailAccountsListHeaderProps {
  totalAccounts: number;
  syncingCount: number;
  errorCount: number;
  pausedCount: number;
  onAdd: () => void;
  addLabel: string;
}

const EmailAccountsListHeader: React.FC<EmailAccountsListHeaderProps> = ({
  totalAccounts,
  syncingCount,
  errorCount,
  pausedCount,
  onAdd,
  addLabel,
}) => {
  const { colors } = useTheme();

  return (
    <div className={cn('rounded-xl border overflow-hidden', colors.border, colors.surface)}>
      <div className="flex flex-col gap-4 border-b px-4 py-4 sm:px-5 sm:py-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className={cn('text-lg font-semibold tracking-tight', colors.text)}>Email accounts</h1>
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium tabular-nums',
                colors.surfaceHover,
                colors.textSecondary,
              )}
            >
              {totalAccounts} total
            </span>
          </div>
          <p className={cn('max-w-2xl text-sm leading-relaxed', colors.textMuted)}>
            Manage incoming mailboxes, sync settings, send-as identities, and sharing with other users.
          </p>
        </div>

        <Button type="button" onClick={onAdd} className="shrink-0 self-start">
          <PlusIcon className="h-5 w-5" />
          {addLabel}
        </Button>
      </div>

      {totalAccounts > 0 && (
        <div className="flex flex-wrap gap-2 px-4 py-3 sm:px-5">
          <SummaryChip label="Syncing" count={syncingCount} tone="info" />
          <SummaryChip label="Errors" count={errorCount} tone="error" />
          <SummaryChip label="Paused" count={pausedCount} tone="muted" />
        </div>
      )}
    </div>
  );
};

function SummaryChip({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: 'info' | 'error' | 'muted';
}) {
  const { colors } = useTheme();
  if (count === 0) return null;

  const toneClass =
    tone === 'error'
      ? 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300'
      : tone === 'info'
        ? 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300'
        : cn(colors.border, colors.textSecondary, colors.surfaceHover);

  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium', toneClass)}>
      {label}
      <span className="rounded-md bg-black/5 px-1.5 py-0 text-[11px] tabular-nums dark:bg-white/10">{count}</span>
    </span>
  );
}

export default EmailAccountsListHeader;
